import { inject, Injectable } from '@angular/core';
import { UtilitiesService } from '@services/utilities.service';
import { EnvironmentService } from '@services/environment.service';
import { MixpanelProperties } from '@core/types/mixpanel.interface';
import { Mixpanel } from 'mixpanel-browser';
import { MixpanelUser } from '@core/types/user.interface';
import { AuthenticationService } from '@app/core/auth';
import { toMixpanelUser } from '@core/mappers/user-to-mixpanel.mapper';

@Injectable({ providedIn: 'root' })
export class MixpanelService {
  private readonly authService = inject(AuthenticationService);
  private readonly utilitiesService = inject(UtilitiesService);
  private readonly environmentService = inject(EnvironmentService);
  private mixpanel: Mixpanel | null = null;
  private initialized = false;

  async init(): Promise<void> {
    if (this.utilitiesService.isDevelopmentMode() || this.initialized) {
      return;
    }

    try {
      const { default: mixpanelInstance } = await import('mixpanel-browser');
      const { token, debug } = this.environmentService.mixpanel;
      this.mixpanel = mixpanelInstance;
      this.mixpanel.init(token, { debug }, 'mixpanel');
      this.initialized = true;
    } catch (err) {
      console.error('Error loading Mixpanel:', err);
    }
  }

  track(event: string, properties: MixpanelProperties): void {
    if (!this.initialized || !this.mixpanel) {
      return;
    }
    try {
      const platform = this.utilitiesService.getPlatform();
      const prefix = this.environmentService.mixpanel.eventNamePrefix;
      const name = `${prefix}_${platform}_${event}`;
      const userProperties = this.getMixpanelUserProperties();
      const finalProperties = { ...properties, ...userProperties };
      this.mixpanel.track(name, finalProperties);
    } catch (err) {
      console.error('Error tracking Mixpanel event:', err);
    }
  }

  private getMixpanelUserProperties(): Partial<MixpanelUser> {
    const currentUserInfo = this.authService.currentUser()?.user;
    if (!currentUserInfo) return {};

    return toMixpanelUser(currentUserInfo);
  }
}
